import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { dueLabel, isOverdue } from '../CallTasksTab/callTaskFormat';
import { hasEnoughData } from '../JourneysTab/stats';

import {
  CallTask,
  CallTaskOutcome,
  JourneyStatsWindow,
  JourneyStep,
  LifecycleJourney,
} from '@/services/lifecycleAPI';

// The Lifecycle journeys page (design: GravyWorkers Prototype, Lifecycle
// journeys screen). Pure helpers, so the page components stay thin.

const EASTERN = 'America/New_York';

export type IconChannel = 'sms' | 'email' | 'push' | 'call';

export const CHANNEL_NAMES: Record<IconChannel, string> = {
  sms: 'SMS',
  email: 'Email',
  push: 'Push',
  call: 'Call',
};

export const reachLine = (count?: number | null, total?: number | null) => {
  const n = count ?? 0;
  const all = total ?? 0;
  const pct = all > 0 ? Math.round((n / all) * 100) : 0;
  return `${n.toLocaleString('en-US')} (${pct}%)`;
};

export const holdoutLine = (journeys: LifecycleJourney[]): string => {
  const values = Array.from(new Set(journeys.map((j) => j.holdout_percent)));
  return values.length === 1
    ? `${values[0]}% holdout per journey`
    : 'Holdout set per journey (0–50%)';
};

const validDate = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const etTime = (iso?: string | null): string => {
  const date = validDate(iso);
  return date ? `${formatInTimeZone(date, EASTERN, 'h:mm a')} ET` : '-';
};

export const etDateTime = (iso?: string | null): string => {
  const date = validDate(iso);
  return date ? `${formatInTimeZone(date, EASTERN, 'MMM d, h:mm a')} ET` : '-';
};

export const etWhen = (iso?: string | null, now: Date = new Date()) => {
  const date = validDate(iso);
  if (!date) return '-';
  const day = (value: Date) => formatInTimeZone(value, EASTERN, 'yyyy-MM-dd');
  return day(date) === day(now) ? etTime(iso) : etDateTime(iso);
};

// cohort_start_on is a date with no time: read it as that calendar day. With
// the year, as in Engine settings: a 2025 date read like a 2026 one (RT-13).
export const cohortDay = (iso?: string | null): string => {
  const date = validDate(iso ? `${iso}T12:00:00Z` : null);
  return date ? formatInTimeZone(date, 'UTC', 'MMM d, yyyy') : '';
};

export const stepChannel = (step: JourneyStep): IconChannel => {
  if (step.kind === 'call_task' || step.channel === 'ops') return 'call';
  if (step.push_only || step.channel === 'push') return 'push';
  return step.channel === 'email' ? 'email' : 'sms';
};

// The design writes the first hour as "1h" and later steps as "Day N". A
// goal message (J8's j8_w) has no day: it goes when the worker reaches the goal.
export const stepWhen = (step: JourneyStep): string => {
  if (step.kind === 'goal_message') return 'On goal';
  return step.offset_hours > 0 && step.offset_hours < 24
    ? `${step.offset_hours}h`
    : `Day ${step.day}`;
};

// Scheduled steps in time order, then the goal message.
export const cadenceSteps = (steps: JourneyStep[]): JourneyStep[] =>
  [...steps].sort(
    (a, b) =>
      Number(a.kind === 'goal_message') - Number(b.kind === 'goal_message') ||
      a.offset_hours - b.offset_hours,
  );

// graphy sends %{first_name}; the design shows {first_name}.
export const previewText = (preview?: string | null): string =>
  (preview ?? '').replace(/%\{(\w+)\}/g, '{$1}');

const pct = (rate: number) => Math.round(rate * 100);

const rates = (stats?: JourneyStatsWindow) =>
  stats &&
  stats.conversion_journey !== null &&
  stats.conversion_holdout !== null
    ? [pct(stats.conversion_journey), pct(stats.conversion_holdout)]
    : null;

export const NOT_ENOUGH_DATA = 'Not enough data yet';

// Same minimum as Engine settings: 30 per arm and 5 goals.
export const goalLine = (stats?: JourneyStatsWindow): string => {
  if (stats && !hasEnoughData(stats)) return NOT_ENOUGH_DATA;
  const both = rates(stats);
  return both ? `${both[0]}% vs ${both[1]}%` : '—';
};

// Lift in percentage points, from the rounded rates the row shows.
export const liftOf = (
  stats?: JourneyStatsWindow,
): { text: string; up: boolean } => {
  const both = stats && hasEnoughData(stats) ? rates(stats) : null;
  if (!both) return { text: '—', up: false };
  const points = both[0] - both[1];
  return points > 0
    ? { text: `+${points} pts`, up: true }
    : { text: `${points} pts`, up: false };
};

// Where each journey step's link goes (graphy Lifecycle::Journeys, the
// link_target of each msg()). The payload does not carry it, so it is listed
// here for the email "Links to" line.
const LINK_TARGETS: Record<string, string> = {
  current_step: 'Worker’s next profile step',
  tax_guide: 'Tax guide',
  bg_check: 'Background check',
  i9_docs: 'I-9 documents',
  shift_feed: 'Shifts near the worker',
  rating: 'Rate the shift',
  business_job: 'Open shifts at the same business',
};

const STEP_TARGETS: Record<string, keyof typeof LINK_TARGETS> = {
  j2_3: 'tax_guide',
  j4_1: 'bg_check',
  j4_2: 'bg_check',
  j4_3: 'bg_check',
  j5_2: 'i9_docs',
  j6_1: 'shift_feed',
  j6_2: 'shift_feed',
  j6_3: 'shift_feed',
  j7_1: 'rating',
  j7_2: 'business_job',
  j7_3: 'shift_feed',
  j8_1: 'shift_feed',
  j8_2: 'shift_feed',
  j8_3: 'shift_feed',
  j8_w: 'shift_feed',
};

export const linkTargetLabel = (stepKey: string): string =>
  LINK_TARGETS[STEP_TARGETS[stepKey] ?? 'current_step'];

const SHIFT_JOURNEYS = ['J6', 'J7', 'J8'];
const DOCUMENT_JOURNEYS = ['J2', 'J4', 'J5'];

// Preferred skills and days only matter when the call is about booking.
export const isShiftJourney = (journeyKey: string) =>
  SHIFT_JOURNEYS.includes(journeyKey);

// The design's outcome list per journey (graphy LifecycleCallTask::OUTCOMES).
export const outcomeOptions = (journeyKey: string): CallTaskOutcome[] => {
  if (isShiftJourney(journeyKey) || journeyKey === 'J9') {
    return ['booked', 'not_interested', 'no_answer'];
  }
  const base: CallTaskOutcome[] = [
    'completed_on_call',
    'will_finish',
    'not_interested',
    'no_answer',
  ];
  return DOCUMENT_JOURNEYS.includes(journeyKey)
    ? [...base, 'document_issue']
    : base;
};

export const dueText = (
  task: CallTask,
  now: Date = new Date(),
): { text: string; overdue: boolean } => {
  if (isOverdue(task, now)) return { text: dueLabel(task, now), overdue: true };
  const due = new Date(task.due_at);
  const day = (date: Date) => formatInTimeZone(date, EASTERN, 'yyyy-MM-dd');
  const time = `${formatInTimeZone(due, EASTERN, 'h:mm a')} ET`;
  if (day(due) === day(now)) return { text: `Today,\n${time}`, overdue: false };
  if (day(due) === day(addDays(now, 1))) {
    return { text: `Tomorrow,\n${time}`, overdue: false };
  }
  return {
    text: `${formatInTimeZone(due, EASTERN, 'MMM d')},\n${time}`,
    overdue: false,
  };
};

// Call scripts carry [NAME] where the caller says their own name; the design
// shows it as a bold "[YOUR NAME]".
export const scriptParts = (script: string): string[] => script.split('[NAME]');
